import { merge } from 'lodash';
import * as clientCache from '../clientCache';
import { count } from '../monitor';
import { SourceInfo } from '../serverTranslate';
import { DEFAULT_TIMEOUT_POLICY } from './constants';
import { createAxiosInstance, retryInterceptor } from './axios';

export interface ClientTranslateProps {
    url: string;
    text: string | string[] | unknown;
    targetLocale: string;
    useClientCache?: boolean;
    sourceInfo: SourceInfo;
    retry?: boolean;
    timeoutPolicy?: string;
}

export const clientTranslate = (options: ClientTranslateProps) => {
    const {
        url,
        text,
        targetLocale,
        useClientCache = false,
        sourceInfo,
        retry = false,
        timeoutPolicy = DEFAULT_TIMEOUT_POLICY,
    } = options;

    const formattedTargetLocale = formatLocale(targetLocale);

    if (useClientCache) {
        const cacheData = clientCache.get({ text: text as string, targetLocale: formattedTargetLocale });
        if (cacheData) {
            monitorCache(sourceInfo.name, 'hit');
            return Promise.resolve(cacheData);
        } else {
            monitorCache(sourceInfo.name, 'miss');
        }
    }

    const postParams = merge(options, {
        text,
        targetLocale: formattedTargetLocale,
        sourceInfo,
        timeoutPolicy,
    });

    const axiosInstance = createAxiosInstance();
    if (retry) {
        retryInterceptor(axiosInstance);
    }

    return axiosInstance.post(url, postParams).then(({ data, status }) => {
        if (useClientCache && status === 200) {
            clientCache.set({ text: text as string, targetLocale, data });
        }

        return { ...data, status };
    });
};

export const formatLocale = (locale: string): string => {
    if (!locale || locale === 'auto') {
        return locale;
    }
    return locale.substr(0, 2).toLowerCase();
};

const monitorCache = (source: string, key: string) => {
    count(`source.${source}.clientCache.${key}`);
};

export const convertStatusNameToHttpCode = (status: string | number): number => {
    const statusesMap = {
        Success: 200,
        Failure: 500,
        Partial: 207,
    };

    return statusesMap[status] || status;
};
